import React, { useEffect, useState, useRef } from 'react';
import { Timer, Star } from 'lucide-react';
interface GameBoardProps {
  level: number;
  onLevelComplete: (timeTaken: number) => void;
}
const DECOYS = ['🎈', '🌸', '🍕', '🎯', '🌈', '🦋', '🎪', '🍩', '🎵', '🔮', '🌺', '🎨', '🍭', '🪐', '🎃', '🦄', '🌻', '🎸', '🍀', '🐙', '🍎', '🚗', '💎', '🔔', '🌙'];
const TARGET = '⭐';
const EMOJI_SIZE = 48; // px
interface EmojiData {
  id: number;
  char: string;
  x: number;
  y: number;
  dx: number;
  dy: number;
  isTarget: boolean;
  rotation: number;
  dRotation: number;
}
export const GameBoard: React.FC<GameBoardProps> = ({
  level,
  onLevelComplete
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const emojisRef = useRef<EmojiData[]>([]);
  const emojiElementsRef = useRef<(HTMLDivElement | null)[]>([]);
  const requestRef = useRef<number>();
  const startTimeRef = useRef<number>(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isBursting, setIsBursting] = useState(false);
  const [burstPos, setBurstPos] = useState({
    x: 0,
    y: 0
  });
  // Initialize level
  useEffect(() => {
    if (!containerRef.current) return;
    const {
      clientWidth,
      clientHeight
    } = containerRef.current;
    // Level difficulty scaling
    const numDecoys = 20 + level * 5;
    const baseSpeed = 1 + level * 0.3;
    const newEmojis: EmojiData[] = [];
    // Add target
    newEmojis.push({
      id: 0,
      char: TARGET,
      x: Math.random() * (clientWidth - EMOJI_SIZE),
      y: Math.random() * (clientHeight - EMOJI_SIZE),
      dx: (Math.random() - 0.5) * baseSpeed * 2,
      dy: (Math.random() - 0.5) * baseSpeed * 2,
      isTarget: true,
      rotation: Math.random() * 360,
      dRotation: (Math.random() - 0.5) * 2
    });
    // Add decoys
    for (let i = 1; i <= numDecoys; i++) {
      const char = DECOYS[Math.floor(Math.random() * DECOYS.length)];
      newEmojis.push({
        id: i,
        char,
        x: Math.random() * (clientWidth - EMOJI_SIZE),
        y: Math.random() * (clientHeight - EMOJI_SIZE),
        dx: (Math.random() - 0.5) * baseSpeed * 2,
        dy: (Math.random() - 0.5) * baseSpeed * 2,
        isTarget: false,
        rotation: Math.random() * 360,
        dRotation: (Math.random() - 0.5) * 2
      });
    }
    emojisRef.current = newEmojis;
    startTimeRef.current = Date.now();
    setElapsedTime(0);
    setIsBursting(false);
    // Timer interval for UI
    const timerInterval = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 100);
    return () => clearInterval(timerInterval);
  }, [level]);
  // Animation loop
  useEffect(() => {
    const animate = () => {
      if (!containerRef.current || isBursting) return;
      const {
        clientWidth,
        clientHeight
      } = containerRef.current;
      const maxX = clientWidth - EMOJI_SIZE;
      const maxY = clientHeight - EMOJI_SIZE;
      emojisRef.current.forEach((emoji, index) => {
        // Update position
        emoji.x += emoji.dx;
        emoji.y += emoji.dy;
        emoji.rotation += emoji.dRotation;
        // Bounce off walls
        if (emoji.x <= 0) {
          emoji.x = 0;
          emoji.dx *= -1;
        } else if (emoji.x >= maxX) {
          emoji.x = maxX;
          emoji.dx *= -1;
        }
        if (emoji.y <= 0) {
          emoji.y = 0;
          emoji.dy *= -1;
        } else if (emoji.y >= maxY) {
          emoji.y = maxY;
          emoji.dy *= -1;
        }
        // Apply transform directly to DOM node for performance
        const el = emojiElementsRef.current[index];
        if (el) {
          el.style.transform = `translate(${emoji.x}px, ${emoji.y}px) rotate(${emoji.rotation}deg)`;
        }
      });
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [isBursting]);
  const handleEmojiClick = (emoji: EmojiData, index: number) => {
    if (isBursting) return;
    if (emoji.isTarget) {
      // Success!
      const timeTaken = Date.now() - startTimeRef.current;
      setBurstPos({
        x: emoji.x + EMOJI_SIZE / 2,
        y: emoji.y + EMOJI_SIZE / 2
      });
      setIsBursting(true);
      // Wait for burst animation before advancing
      setTimeout(() => {
        onLevelComplete(timeTaken);
      }, 800);
    } else {
      // Wrong click - shake effect
      const el = emojiElementsRef.current[index];
      if (el) {
        // Remove and re-add class to trigger animation again if clicked multiple times
        el.classList.remove('shake');
        void el.offsetWidth; // trigger reflow
        el.classList.add('shake');
        // Optional: flash red slightly
        el.style.filter = 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.8))';
        setTimeout(() => {
          if (el) el.style.filter = '';
        }, 400);
      }
    }
  };
  return <div className="flex flex-col h-screen bg-slate-950 overflow-hidden" data-id="element-4">
      {/* Header */}
      <header className="flex items-center justify-between p-4 bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 z-10" data-id="element-5">
        <div className="flex items-center gap-2" data-id="element-6">
          <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" data-id="element-7" />
          <span className="text-xl font-bold text-white" data-id="element-8">Level {level}</span>
        </div>

        <div className="flex items-center gap-2 text-cyan-400 bg-slate-800/50 px-4 py-2 rounded-full" data-id="element-9">
          <Timer className="w-5 h-5" data-id="element-10" />
          <span className="font-mono text-lg font-semibold" data-id="element-11">
            {(elapsedTime / 1000).toFixed(1)}s
          </span>
        </div>
      </header>

      {/* Game Area */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden touch-none" data-id="element-12">
        {/* Emojis */}
        {emojisRef.current.map((emoji, index) => <div key={emoji.id} ref={el => emojiElementsRef.current[index] = el} onClick={() => handleEmojiClick(emoji, index)} className={`absolute top-0 left-0 text-4xl md:text-5xl cursor-pointer select-none transition-opacity duration-300 ${isBursting && !emoji.isTarget ? 'opacity-0' : 'opacity-100'}`} style={{
        width: EMOJI_SIZE,
        height: EMOJI_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `translate(${emoji.x}px, ${emoji.y}px) rotate(${emoji.rotation}deg)`,
        willChange: 'transform',
        zIndex: emoji.isTarget ? 10 : 1 // Ensure target isn't always buried
      }} data-id="element-13">
            {emoji.char}
          </div>)}

        {/* Success Burst Effect */}
        {isBursting && <div className="absolute pointer-events-none" style={{
        left: burstPos.x,
        top: burstPos.y,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-14">
            <div className="relative" data-id="element-15">
              <div className="absolute inset-0 bg-yellow-400 rounded-full animate-ping opacity-75" style={{
            width: 100,
            height: 100,
            left: -50,
            top: -50,
            animationDuration: '0.8s'
          }} data-id="element-16"></div>
              <div className="text-6xl animate-bounce" data-id="element-17">⭐</div>

              {/* Particles */}
              {[...Array(8)].map((_, i) => <div key={i} className="absolute text-2xl" style={{
            left: 0,
            top: 0,
            animation: `particle-${i} 0.8s ease-out forwards`,
            transformOrigin: 'center'
          }} data-id="element-18">
                  ✨
                </div>)}
              <style data-id="element-19">{`
                ${[...Array(8)].map((_, i) => `
                  @keyframes particle-${i} {
                    0% { transform: rotate(${i * 45}deg) translateY(0) scale(1); opacity: 1; }
                    100% { transform: rotate(${i * 45}deg) translateY(-100px) scale(0); opacity: 0; }
                  }
                `).join('\n')}
              `}</style>
            </div>
          </div>}
      </div>
    </div>;
};